/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { assetPrefix, type Service } from '@ahm/api-wrappers-core';
import {
  Box,
  Button,
  Card,
  Image,
  rem,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { openContextModal } from '@mantine/modals';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import InfoToolTipIcon from '@/assets/icons/info-tooltip-icon.svg';
import { ModalName } from '@/components/modals';
import { useDetectBanHour } from '@/hooks';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type TruckServiceItemProps = {
  service: Service;
  onSelect: () => void;
};

function TruckServiceItem({ service, onSelect }: TruckServiceItemProps) {
  const isMobile = useIsMobile();
  const locale = useLocaleSuffix();
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const { selectedService } = useBookingStore((state) => state);
  const { checkServiceInBanFn } = useDetectBanHour();
  const { estimatedData } = useBookingStore((state) => state);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const isActive = useMemo(() => {
    return selectedService?._id === service._id;
  }, [selectedService?._id, service]);

  const isDisabled = useMemo(() => {
    const isBanHour = checkServiceInBanFn(service);
    const estimateServiceId = estimatedData?.service_id;

    if (
      isBanHour ||
      (estimatedData?.error && estimateServiceId === service._id)
    ) {
      return true;
    }

    return false;
  }, [service, estimatedData, checkServiceInBanFn]);

  const handleViewVehicleInfo = () => {
    openContextModal({
      modal: ModalName.TruckServiceDetails,
      innerProps: {
        service,
      },
      h: '100%',
      closeOnClickOutside: false,
      closeOnEscape: false,
      withCloseButton: true,
      centered: true,
      fullScreen: isMobile,
      w: '100%',
      zIndex: 1001,
      title: 'Chi tiết xe',

      styles: {
        root: { zIndex: 1001 },
        inner: {
          padding: 0,
        },
        title: {
          textAlign: 'center',
          width: '100%',
          fontSize: 18,
          fontWeight: 700,
        },
        header: {
          padding: 0,
          minHeight: 44,
          backgroundColor: 'white',
        },
        close: {
          outline: 'none',
          position: 'absolute',
          ...(isMobile
            ? {
                left: rem(16),
              }
            : {
                right: rem(16),
              }),
        },

        body: {
          position: 'relative',
          padding: 0,
          backgroundColor: 'var(--mantine-color-neutral-0)',
        },
      },
    });
  };

  return (
    <Box
      pos="relative"
      h="100%"
      w="100%"
      style={{
        borderRadius: rem(12),
        border: `1px solid ${isActive && isDisabled ? 'var(--mantine-color-red-6)' : isActive ? 'var(--mantine-color-primary-7)' : 'var(--mantine-color-neutral-1)'}`,
      }}
      onClick={onSelect}
    >
      <Card
        h="100%"
        p={rem(8)}
        display="flex"
        pos="relative"
        overflow="hidden"
        style={() => ({
          cursor: isDisabled ? 'not-allowed' : isActive ? 'default' : 'pointer',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
          backgroundColor:
            (isHovered && !isActive) || isDisabled
              ? 'var(--mantine-color-neutral-0)'
              : isActive
                ? 'var(--mantine-color-primary-1)'
                : 'white',
          borderRadius: rem(12),
          transition: 'border-color 0.3s ease',
          opacity: isDisabled ? 0.5 : 1,
        })}
        disabled={isDisabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Card.Section
          h={
            service?.vehicle_info?.image_url
              ? isHovered || isActive
                ? rem(42)
                : rem(44)
              : 'auto'
          }
          w={rem(76)}
          pos="relative"
          pt={isHovered || isActive ? rem(16) : 0}
          mb={isMobile ? 0 : isActive || isHovered ? 8 : 'md'}
          sx={{ flex: 'none' }}
        >
          <Image
            src={
              service?.vehicle_info?.image_url ||
              `${assetPrefix}/static/icons/truck-order.svg`
            }
            height="100%"
            alt={service[`name_${locale}`]}
            fit="contain"
          />
        </Card.Section>

        <Stack
          gap={2}
          align="center"
          opacity={isActive || isHovered || !isMobile ? 1 : 0}
        >
          <Title
            order={6}
            c="neutral.7"
            w="100%"
            fw={isMobile ? 700 : 600}
            ta={isMobile ? 'left' : 'center'}
            lineClamp={1}
          >
            {service[`name_${locale}`]}
          </Title>

          {service.vehicle_info && (isHovered || isActive) ? (
            <>
              <Text
                size="xs"
                align={isMobile ? 'left' : 'center'}
                c="neutral.7"
                lh={rem(16)}
                lineClamp={isMobile ? 1 : 2}
                w="100%"
                mah={32}
                weight={500}
                sx={{ overflow: 'hidden' }}
              >
                {
                  service.vehicle_info[
                    `short_intro_${locale === 'vi_vn' ? 'vi' : 'en'}'}`
                  ]
                }
              </Text>
              <Text
                size="xs"
                align={isMobile ? 'left' : 'center'}
                c="neutral.7"
                lh={rem(16)}
                lineClamp={isMobile ? 1 : 2}
                w="100%"
                mah={32}
                weight={500}
                sx={{ overflow: 'hidden' }}
              >
                {
                  service.vehicle_info[
                    `short_description_${locale === 'vi_vn' ? 'vi' : 'en'}`
                  ]
                }
              </Text>
            </>
          ) : undefined}

          {isActive || isHovered ? (
            <Button
              variant="transparent"
              size="compact-sm"
              fw={700}
              color="var(--mantine-color-primary-7)"
              sx={{
                flex: 'none',
                '&:hover': {
                  background: 'transparent',
                },
              }}
              leftSection={<InfoToolTipIcon size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                handleViewVehicleInfo();
              }}
            >
              Xem chi tiết
            </Button>
          ) : undefined}
        </Stack>
      </Card>

      <Box
        pos="absolute"
        top={rem(-8)}
        right={rem(-8)}
        style={{
          display: isActive ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',

          backgroundColor: isDisabled
            ? 'var(--mantine-color-red-6)'
            : 'var(--mantine-color-primary-6)',
          width: rem(24),
          height: rem(24),
          borderRadius: '50%',
          padding: rem(6),
        }}
      >
        <Check color="#fff" />
      </Box>
    </Box>
  );
}

export default TruckServiceItem;
